import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Droplets, Calculator, Activity, Utensils, Coffee, Clock, CheckCircle2, ShieldCheck, FileText, ListChecks, ShoppingCart, XIcon } from 'lucide-react';
const FadeInUp = ({
  children,
  delay = 0
}: {
  children: React.ReactNode;
  delay?: number;
}) => <motion.div initial={{
  opacity: 0,
  y: 30
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-50px'
}} transition={{
  duration: 0.6,
  delay,
  ease: 'easeOut'
}} data-id="element-0">
    {children}
  </motion.div>;
const PRIVACY_CONTENT = `
Última actualización: Abril 2026

En Método Anti-Inflamatorio 7D, respetamos tu privacidad. Esta política describe cómo recopilamos, usamos y protegemos tu información personal.

**Información que recopilamos:**
• Datos de compra: nombre, correo electrónico y datos de pago procesados de forma segura a través de PayPal.
• No almacenamos información de tarjetas de crédito ni datos financieros sensibles.

**Uso de la información:**
• Para procesar tu compra y entregar el producto digital.
• Para enviarte actualizaciones relacionadas con tu compra (si aplica).
• No vendemos, alquilamos ni compartimos tu información personal con terceros.

**Protección de datos:**
• Utilizamos medidas de seguridad estándar de la industria para proteger tu información.
• Los pagos son procesados de forma segura a través de PayPal.

**Cookies:**
• Podemos utilizar cookies básicas para mejorar tu experiencia de navegación.

**Tus derechos:**
• Puedes solicitar la eliminación de tus datos en cualquier momento.
• Puedes optar por no recibir comunicaciones de marketing.

**Cambios a esta política:**
• Nos reservamos el derecho de actualizar esta política. Los cambios serán publicados en esta página.
`.trim();
const TERMS_CONTENT = `
Última actualización: Abril 2026

Al comprar y utilizar el Método Anti-Inflamatorio 7D, aceptas los siguientes términos y condiciones.

**Producto digital:**
• Este es un producto digital (PDF) que se entrega electrónicamente después de la compra.
• No se ofrecen reembolsos una vez entregado el producto digital, salvo que la ley aplicable lo requiera.

**Uso personal:**
• El contenido es para uso personal únicamente.
• No está permitido redistribuir, revender o compartir el material sin autorización.

**Descargo de responsabilidad médica:**
• Este producto es solo para fines informativos y educativos.
• No sustituye el consejo médico profesional, diagnóstico o tratamiento.
• Consulta siempre a un profesional de la salud antes de comenzar cualquier programa de alimentación o bienestar.
• Los resultados pueden variar de persona a persona.

**Propiedad intelectual:**
• Todo el contenido, diseño y material del Método Anti-Inflamatorio 7D está protegido por derechos de autor.

**Limitación de responsabilidad:**
• No nos hacemos responsables de resultados individuales ni de decisiones tomadas basándose en el contenido del producto.

**Ley aplicable:**
• Estos términos se rigen por las leyes aplicables de los Estados Unidos.
`.trim();
export function App() {
  const [modalOpen, setModalOpen] = useState<'privacy' | 'terms' | null>(null);
  return <div className="min-h-screen bg-cream font-sans text-charcoal selection:bg-sage selection:text-white" data-id="element-1">
      {/* 1. HERO SECTION & 2. VISUAL ASSET */}
      <section className="pt-12 pb-16 px-6 md:pt-20 md:pb-24 max-w-4xl mx-auto text-center" data-id="element-2">
        <FadeInUp data-id="element-3">
          <span className="inline-block py-1 px-3 rounded-full bg-sage/10 text-sage font-semibold text-sm tracking-wide mb-6" data-id="element-4">
            Exclusivo para la mujer Latina en USA
          </span>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6 text-charcoal" data-id="element-5">
            ¿Te sientes "Bloated" e inflamada al final del día?
          </h1>
          <p className="text-lg md:text-xl text-charcoal/80 mb-10 max-w-2xl mx-auto leading-relaxed" data-id="element-6">
            Descubre el{' '}
            <strong className="text-terracotta font-semibold" data-id="element-7">
              Protocolo Cortisol-Reset
            </strong>
            : Cómo desinflamar tu abdomen y recuperar tu ligereza en solo 7 días
            (Sin dietas extremas y con ingredientes de tu supermercado local).
          </p>
        </FadeInUp>

        <FadeInUp delay={0.2} data-id="element-8">
          <div className="relative mx-auto max-w-md mb-12" data-id="element-9">
            <div className="absolute inset-0 bg-sage/20 rounded-3xl transform rotate-3 scale-105 -z-10" data-id="element-10"></div>
            <img src="https://cdn.magicpatterns.com/uploads/7oz3NzG6K5ijRhF5PVC68J/heylloyd-image-1775268223226.png" alt="Método Anti-Inflamatorio 7D en tablet" className="w-full h-auto rounded-2xl shadow-xl object-cover" data-id="element-11" />
          </div>
        </FadeInUp>

        <FadeInUp delay={0.3} data-id="element-12">
          <a href="#oferta" onClick={e => {
          e.preventDefault();
          document.getElementById('oferta')?.scrollIntoView({
            behavior: 'smooth'
          });
        }} className="w-full md:w-auto bg-terracotta hover:bg-[#b06b52] text-white font-bold text-lg py-4 px-10 rounded-xl shadow-lg shadow-terracotta/30 transition-all transform hover:-translate-y-1 flex items-center justify-center mx-auto group no-underline" data-id="element-13">
            Empezar mi plan
            <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-14" />
          </a>
          <p className="mt-4 text-sm text-charcoal/60 font-medium" data-id="element-15">
            Diseñado para tu estilo de vida en USA
          </p>
        </FadeInUp>
      </section>

      {/* 3. PROBLEM SECTION (EMPATHY) */}
      <section className="py-16 px-6 bg-white" data-id="element-16">
        <div className="max-w-3xl mx-auto" data-id="element-17">
          <FadeInUp data-id="element-18">
            <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6 text-center" data-id="element-19">
              Esto no es otra dieta para sufrir
            </h2>
            <p className="text-lg text-charcoal/80 leading-relaxed mb-8" data-id="element-20">
              Son las 11:00 PM después de un día de trabajo, tráfico y mil
              responsabilidades en este país. Estás frente al espejo y ese jean
              que en la mañana te cerraba bien, ahora te aprieta. Sientes una
              pesadez que no se quita ni durmiendo. No es grasa...{' '}
              <strong className="text-charcoal" data-id="element-21">
                es inflamación por estrés (Cortisol)
              </strong>
              .
            </p>

            <div className="bg-rose-50 border border-rose-100 rounded-2xl p-6 md:p-8 shadow-sm" data-id="element-22">
              <p className="text-terracotta font-semibold text-lg md:text-xl text-center" data-id="element-23">
                El problema real: Barriga hinchada, pesadez por la tarde, ropa
                que molesta.
              </p>
            </div>
          </FadeInUp>
        </div>
      </section>

      {/* 4. FAILURE ANALYSIS SECTION */}
      <section className="py-16 px-6 bg-cream" data-id="element-24">
        <div className="max-w-4xl mx-auto" data-id="element-25">
          <FadeInUp data-id="element-26">
            <h2 className="font-serif text-3xl md:text-4xl font-bold mb-12 text-center" data-id="element-27">
              Por qué lo que probaste falla
            </h2>
          </FadeInUp>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-28">
            <FadeInUp delay={0.1} data-id="element-29">
              <div className="bg-white p-8 rounded-2xl shadow-sm h-full border border-black/5" data-id="element-30">
                <div className="w-12 h-12 bg-rose-50 rounded-full flex items-center justify-center mb-6 text-terracotta" data-id="element-31">
                  <Droplets className="w-6 h-6" data-id="element-32" />
                </div>
                <h3 className="font-bold text-xl mb-3" data-id="element-33">Juice Cleanses</h3>
                <p className="text-charcoal/70" data-id="element-34">
                  Exceso de azúcar que irrita tu intestino.
                </p>
              </div>
            </FadeInUp>

            <FadeInUp delay={0.2} data-id="element-35">
              <div className="bg-white p-8 rounded-2xl shadow-sm h-full border border-black/5" data-id="element-36">
                <div className="w-12 h-12 bg-rose-50 rounded-full flex items-center justify-center mb-6 text-terracotta" data-id="element-37">
                  <Calculator className="w-6 h-6" data-id="element-38" />
                </div>
                <h3 className="font-bold text-xl mb-3" data-id="element-39">Contar Calorías</h3>
                <p className="text-charcoal/70" data-id="element-40">
                  Estresa tus hormonas y retienes más agua.
                </p>
              </div>
            </FadeInUp>

            <FadeInUp delay={0.3} data-id="element-41">
              <div className="bg-white p-8 rounded-2xl shadow-sm h-full border border-black/5" data-id="element-42">
                <div className="w-12 h-12 bg-rose-50 rounded-full flex items-center justify-center mb-6 text-terracotta" data-id="element-43">
                  <Activity className="w-6 h-6" data-id="element-44" />
                </div>
                <h3 className="font-bold text-xl mb-3" data-id="element-45">Cardio Intenso</h3>
                <p className="text-charcoal/70" data-id="element-46">
                  Sube el cortisol y te inflama más.
                </p>
              </div>
            </FadeInUp>
          </div>
        </div>
      </section>

      {/* 5. THE UNIQUE MECHANISM (3-3-3) */}
      <section className="py-16 px-6 bg-white" data-id="element-47">
        <div className="max-w-3xl mx-auto" data-id="element-48">
          <FadeInUp data-id="element-49">
            <h2 className="font-serif text-3xl md:text-4xl font-bold mb-12 text-center" data-id="element-50">
              7 días con el Protocolo 3-3-3
            </h2>
          </FadeInUp>

          <div className="space-y-4" data-id="element-51">
            <FadeInUp delay={0.1} data-id="element-52">
              <div className="flex items-start p-6 bg-sage/5 border-l-4 border-sage rounded-r-2xl" data-id="element-53">
                <div className="bg-sage text-white p-3 rounded-xl mr-5 shrink-0" data-id="element-54">
                  <Utensils className="w-6 h-6" data-id="element-55" />
                </div>
                <div data-id="element-56">
                  <h3 className="font-bold text-xl mb-2 text-charcoal" data-id="element-57">
                    3 Comidas Inteligentes
                  </h3>
                  <p className="text-charcoal/70" data-id="element-58">
                    Para "enfriar" la inflamación sistémica.
                  </p>
                </div>
              </div>
            </FadeInUp>

            <FadeInUp delay={0.2} data-id="element-59">
              <div className="flex items-start p-6 bg-sage/5 border-l-4 border-sage rounded-r-2xl" data-id="element-60">
                <div className="bg-sage text-white p-3 rounded-xl mr-5 shrink-0" data-id="element-61">
                  <Coffee className="w-6 h-6" data-id="element-62" />
                </div>
                <div data-id="element-63">
                  <h3 className="font-bold text-xl mb-2 text-charcoal" data-id="element-64">
                    3 Infusiones Clave
                  </h3>
                  <p className="text-charcoal/70" data-id="element-65">
                    El drenaje natural que tu cuerpo necesita.
                  </p>
                </div>
              </div>
            </FadeInUp>

            <FadeInUp delay={0.3} data-id="element-66">
              <div className="flex items-start p-6 bg-sage/5 border-l-4 border-sage rounded-r-2xl" data-id="element-67">
                <div className="bg-sage text-white p-3 rounded-xl mr-5 shrink-0" data-id="element-68">
                  <Clock className="w-6 h-6" data-id="element-69" />
                </div>
                <div data-id="element-70">
                  <h3 className="font-bold text-xl mb-2 text-charcoal" data-id="element-71">
                    3 Hábitos de 3 Minutos
                  </h3>
                  <p className="text-charcoal/70" data-id="element-72">
                    Apagan la señal de alerta en tu cerebro.
                  </p>
                </div>
              </div>
            </FadeInUp>
          </div>
        </div>
      </section>

      {/* 6. RESULTS CHECKLIST */}
      <section className="py-16 px-6 bg-cream" data-id="element-73">
        <div className="max-w-3xl mx-auto" data-id="element-74">
          <FadeInUp data-id="element-75">
            <div className="bg-white p-8 md:p-12 rounded-3xl shadow-sm border border-black/5" data-id="element-76">
              <h2 className="font-serif text-3xl md:text-4xl font-bold mb-8 text-center" data-id="element-77">
                Lo que empezarás a notar:
              </h2>

              <ul className="space-y-5" data-id="element-78">
                {['Menos presión en la cintura y abdomen', 'Más energía para tu día a día', 'Digestión ligera después de comer', 'Ingredientes fáciles de comprar (Walmart/Target)'].map((item, index) => <li key={index} className="flex items-center text-lg text-charcoal/80" data-id="element-79">
                    <CheckCircle2 className="w-6 h-6 text-sage mr-4 shrink-0" data-id="element-80" />
                    <span data-id="element-81">{item}</span>
                  </li>)}
              </ul>
            </div>
          </FadeInUp>
        </div>
      </section>

      {/* 7. FINAL OFFER */}
      <section id="oferta" className="py-16 px-6 bg-white" data-id="element-82">
        <div className="max-w-3xl mx-auto text-center" data-id="element-83">
          <FadeInUp data-id="element-84">
            <h2 className="font-serif text-3xl md:text-4xl font-bold mb-10" data-id="element-85">
              ¿Qué incluye exactamente?
            </h2>

            <div className="flex flex-col md:flex-row justify-center items-center gap-6 mb-12" data-id="element-86">
              <div className="flex flex-col items-center" data-id="element-87">
                <div className="w-16 h-16 bg-cream rounded-full flex items-center justify-center mb-3 text-terracotta" data-id="element-88">
                  <FileText className="w-8 h-8" data-id="element-89" />
                </div>
                <span className="font-medium" data-id="element-90">Guía Digital PDF</span>
              </div>
              <div className="hidden md:block text-charcoal/30 text-2xl" data-id="element-91">+</div>
              <div className="flex flex-col items-center" data-id="element-92">
                <div className="w-16 h-16 bg-cream rounded-full flex items-center justify-center mb-3 text-terracotta" data-id="element-93">
                  <ListChecks className="w-8 h-8" data-id="element-94" />
                </div>
                <span className="font-medium" data-id="element-95">El Protocolo 3-3-3</span>
              </div>
              <div className="hidden md:block text-charcoal/30 text-2xl" data-id="element-96">+</div>
              <div className="flex flex-col items-center" data-id="element-97">
                <div className="w-16 h-16 bg-cream rounded-full flex items-center justify-center mb-3 text-terracotta" data-id="element-98">
                  <ShoppingCart className="w-8 h-8" data-id="element-99" />
                </div>
                <span className="font-medium" data-id="element-100">Lista de Compras USA</span>
              </div>
            </div>

            <div className="mb-8 flex flex-col items-center gap-2" data-id="element-101">
              <span className="text-charcoal/50 text-xl line-through" data-id="element-102">
                $19.99 USD
              </span>
              <span className="inline-block bg-sage/10 text-sage font-bold text-3xl md:text-4xl py-4 px-8 rounded-2xl" data-id="element-103">
                Solo $6.36 USD
              </span>
              <span className="inline-block bg-terracotta/10 text-terracotta font-bold text-sm py-1 px-4 rounded-full mt-1" data-id="element-104">
                Ahorras 68% — Oferta limitada
              </span>
            </div>

            <a href="https://www.vitarutinas.com/cart/50330442137846:1" target="_blank" rel="noopener noreferrer" className="w-full md:w-auto bg-terracotta hover:bg-[#b06b52] text-white font-bold text-xl py-5 px-12 rounded-xl shadow-xl shadow-terracotta/30 transition-all transform hover:-translate-y-1 flex items-center justify-center mx-auto group mb-8 no-underline" data-id="element-105">
              OBTENER MI RESET AHORA
              <ArrowRight className="ml-2 w-6 h-6 group-hover:translate-x-1 transition-transform" data-id="element-106" />
            </a>

            <div className="flex items-center justify-center text-charcoal/60 text-sm font-medium" data-id="element-107">
              Pago 100% seguro vía PayPal
            </div>
          </FadeInUp>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-8 border-t border-black/5" data-id="element-108">
        <div className="max-w-3xl mx-auto px-6 flex flex-col items-center gap-4" data-id="element-109">
          <div className="flex items-center gap-4 text-sm" data-id="element-110">
            <button onClick={() => setModalOpen('privacy')} className="text-charcoal/50 hover:text-charcoal transition-colors underline underline-offset-2" data-id="element-111">
              Política de Privacidad
            </button>
            <span className="text-charcoal/20" data-id="element-112">|</span>
            <button onClick={() => setModalOpen('terms')} className="text-charcoal/50 hover:text-charcoal transition-colors underline underline-offset-2" data-id="element-113">
              Términos y Condiciones
            </button>
          </div>
          <p className="text-charcoal/40 text-sm" data-id="element-114">
            © {new Date().getFullYear()} Método Anti-Inflamatorio 7D. Todos los
            derechos reservados.
          </p>
        </div>
      </footer>

      {/* LEGAL MODALS */}
      <AnimatePresence data-id="element-115">
        {modalOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => setModalOpen(null)} data-id="element-116">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.25,
          ease: 'easeOut'
        }} className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[80vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-id="element-117">
              <div className="flex items-center justify-between p-6 border-b border-black/5" data-id="element-118">
                <h3 className="font-serif text-xl font-bold text-charcoal" data-id="element-119">
                  {modalOpen === 'privacy' ? 'Política de Privacidad' : 'Términos y Condiciones'}
                </h3>
                <button onClick={() => setModalOpen(null)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-charcoal/5 transition-colors text-charcoal/60" data-id="element-120">
                  <XIcon className="w-5 h-5" data-id="element-121" />
                </button>
              </div>
              <div className="p-6 overflow-y-auto text-charcoal/80 text-sm leading-relaxed whitespace-pre-line" data-id="element-122">
                {modalOpen === 'privacy' ? PRIVACY_CONTENT : TERMS_CONTENT}
              </div>
              <div className="p-4 border-t border-black/5" data-id="element-123">
                <button onClick={() => setModalOpen(null)} className="w-full py-3 bg-charcoal/5 hover:bg-charcoal/10 rounded-xl text-charcoal font-medium text-sm transition-colors" data-id="element-124">
                  Cerrar
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}